import { observer } from 'mobx-react';

import { useStores } from '../../../../stores/useStores';
import { useMetagameWidgetData } from '../../hooks/useMetagameWidgetData';
import type { MetagameWidgetDataType, WidgetSizeType } from '../../types';
import { WidgetWrapper } from '../WidgetWrapper/WidgetWrapper';

import {
  METAGAME_FEATURES,
  featureBalance,
  formatFeatureBalance,
} from './MetagameFeatureBalance/metagameFeatureBalance';
import { MetagameFeaturePill } from './MetagameFeatureBalance/MetagameFeaturePill';
import { MetagameSmallWidget } from './MetagameSmallWidget/MetagameSmallWidget';
import { MetagameWideWidget } from './MetagameWideWidget/MetagameWideWidget';
import { MetagameWidgetTimer } from './MetagameWidgetTimer/MetagameWidgetTimer';

interface MetagameWidgetProps {
  sizeType: WidgetSizeType;
  data: MetagameWidgetDataType;
}

function MetagameWidgetComponent(props: MetagameWidgetProps) {
  const { coinTypeStore } = useStores();
  const coinType = coinTypeStore.coinType;
  const { data, sizeType } = props;
  const { event, metagameType } = data;
  const eventByCoin = event[coinType.toLowerCase() as 'gc' | 'sc'];

  const { widgetImageUrl, balanceValue, onTimerEnd, handleClick } = useMetagameWidgetData({ event, eventByCoin });

  if (!eventByCoin) {
    return null;
  }

  const feature = METAGAME_FEATURES[metagameType];
  const featureCount = feature ? featureBalance(eventByCoin, feature.featureKey) : 0;
  const shownFeature = feature && featureCount > 0 ? feature : undefined;
  const featureOnly = !!shownFeature && eventByCoin.balance === 0;

  const timer = (
    <MetagameWidgetTimer endDate={new Date(event.endDate)} extended={sizeType === 'wide'} onEnd={onTimerEnd} />
  );

  return (
    <WidgetWrapper smallWidgetExtended dataTestId={data.dataTestId} sizeType={sizeType} onClick={handleClick}>
      {sizeType === 'small' ? (
        <MetagameSmallWidget
          balanceValue={featureOnly ? formatFeatureBalance(featureCount) : balanceValue}
          counterStyle={featureOnly ? shownFeature.colors[coinType] : undefined}
          timer={timer}
          widgetImageUrl={shownFeature?.art[coinType] ?? widgetImageUrl}
        />
      ) : (
        <MetagameWideWidget
          balanceValue={balanceValue}
          extra={
            shownFeature && (
              <MetagameFeaturePill
                balance={featureCount}
                colors={shownFeature.colors[coinType]}
                labelKey={shownFeature.labelKey}
              />
            )
          }
          metagameType={metagameType}
          timer={timer}
          widgetImageUrl={shownFeature?.art[coinType] ?? widgetImageUrl}
        />
      )}
    </WidgetWrapper>
  );
}

export const MetagameWidget = observer(MetagameWidgetComponent);
